'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  ChevronRight,
  CloudOff,
  CloudSun,
  FileText,
  HeartPulse,
  History,
  LineChart,
  LockKeyhole,
  Pencil,
  Printer,
  RefreshCw,
  RotateCcw,
  Trash2,
  UserRound,
  WifiOff,
  X,
} from 'lucide-react';

import {
  addWeather,
  ApiError,
  completeIntervention,
  createEntry,
  deleteEntry,
  getSession,
  listEntries,
  markWeatherUnavailable,
  restoreEntry,
  unlock,
  updateEntry,
} from '@/lib/api';
import {
  BODY_REGIONS,
  emptyDraft,
  type EnteredBy,
  type EntryDraft,
  INTERVENTION_TYPES,
  labelForEnteredBy,
  type NewPainEntry,
  PAIN_TYPES,
  type PainEntry,
  toggleItem,
} from '@/lib/model';
import { listQueuedEntries, queueEntry, removeQueuedEntry } from '@/lib/outbox';
import { entriesWithinDays, summarizeEntries } from '@/lib/summary';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

type View = 'log' | 'history' | 'summary' | 'report';
type Notice = { kind: 'success' | 'warning' | 'error'; text: string; undoId?: string };

const painLevels = Array.from({ length: 11 }, (_, index) => index);
const PAIN_GUIDE = [
  { level: 0, label: 'No pain', description: 'No pain.' },
  { level: 1, label: 'Minimal', description: 'Hardly noticeable.' },
  { level: 2, label: 'Mild', description: 'Noticeable when paying attention, but usually easy to set aside.' },
  { level: 3, label: 'Uncomfortable', description: 'Bothersome, but often possible to ignore.' },
  { level: 4, label: 'Moderate', description: 'Constantly noticeable; most usual activities are still possible.' },
  { level: 5, label: 'Distracting', description: 'Hard to ignore; some activities need to stop.' },
  { level: 6, label: 'Distressing', description: 'Often interrupts activities and is difficult to set aside.' },
  { level: 7, label: 'Hard to manage', description: 'Present most of the time and limits basic activities.' },
  { level: 8, label: 'Intense', description: 'Makes talking, listening, or clear thinking difficult.' },
  { level: 9, label: 'Severe', description: 'Dominates attention; moving or communicating is very difficult.' },
  { level: 10, label: 'Unable to function', description: 'Unable to move or function normally; get urgent help if needed.' },
] as const;

function apiMessage(error: unknown) {
  if (error instanceof ApiError) return error.message;
  return 'Cynthia Health could not reach its private storage. Please try again.';
}

function localTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
}

function createClientId() {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();

  const bytes = new Uint8Array(16);
  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function createPayload(draft: EntryDraft): NewPainEntry {
  const timestamp = new Date().toISOString();
  const painLevel = draft.painLevel ?? 0;
  return {
    id: createClientId(),
    occurredAtUtc: timestamp,
    timezone: localTimezone(),
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    painLevel,
    bodyRegions: draft.bodyRegions,
    otherBodyRegion: draft.otherBodyRegion.trim(),
    painTypes: draft.painTypes,
    enteredBy: draft.enteredBy,
    notes: draft.notes.trim(),
    interventions: draft.interventions.map((type) => ({
      id: createClientId(),
      type: type as (typeof INTERVENTION_TYPES)[number],
      beforePain: painLevel,
      startedAtUtc: timestamp,
    })),
  };
}

function queuedAsEntry(entry: NewPainEntry): PainEntry {
  return {
    ...entry,
    interventions: entry.interventions.map((item) => ({ ...item, afterPain: null, completedAtUtc: null })),
    weather: {
      status: 'pending',
      temperatureC: null,
      apparentTemperatureC: null,
      pressureHpa: null,
      weatherCode: null,
      observedAtUtc: null,
    },
    createdAtUtc: entry.occurredAtUtc,
    updatedAtUtc: entry.occurredAtUtc,
    version: 1,
    syncStatus: 'queued',
  };
}

function replaceEntry(entries: PainEntry[], entry: PainEntry) {
  const without = entries.filter((item) => item.id !== entry.id);
  return [entry, ...without].sort((a, b) => b.occurredAtUtc.localeCompare(a.occurredAtUtc));
}

function bodyRegionsText(entry: Pick<PainEntry, 'bodyRegions' | 'otherBodyRegion'>) {
  return entry.bodyRegions.map((region) => (
    region === 'Other' && entry.otherBodyRegion ? `Other: ${entry.otherBodyRegion}` : region
  )).join(', ');
}

function toggleEntryIntervention(entry: PainEntry, type: (typeof INTERVENTION_TYPES)[number]) {
  const existing = entry.interventions.find((item) => item.type === type);
  if (existing) {
    return { ...entry, interventions: entry.interventions.filter((item) => item.id !== existing.id) };
  }
  return {
    ...entry,
    interventions: [...entry.interventions, {
      id: createClientId(),
      type,
      beforePain: entry.painLevel,
      afterPain: null,
      startedAtUtc: new Date().toISOString(),
      completedAtUtc: null,
    }],
  };
}

function formatWhen(timestamp: string, options?: Intl.DateTimeFormatOptions) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return new Intl.DateTimeFormat('en-US', options ?? {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function fahrenheit(celsius: number) {
  return Math.round((celsius * 9) / 5 + 32);
}

function weatherText(entry: PainEntry) {
  if (entry.weather.status === 'pending') return 'Weather pending';
  if (entry.weather.status === 'unavailable' || entry.weather.temperatureC === null) return 'Weather unavailable';
  const pressure = entry.weather.pressureHpa === null ? '' : ` · ${Math.round(entry.weather.pressureHpa)} hPa`;
  return `${fahrenheit(entry.weather.temperatureC)}°F${pressure}`;
}

function useConnectivity() {
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  useEffect(() => {
    const markOnline = () => setOnline(true);
    const markOffline = () => setOnline(false);
    window.addEventListener('online', markOnline);
    window.addEventListener('offline', markOffline);
    return () => {
      window.removeEventListener('online', markOnline);
      window.removeEventListener('offline', markOffline);
    };
  }, []);
  return online;
}

export function CynthiaApp() {
  const [session, setSession] = useState<'checking' | 'locked' | 'ready'>('checking');
  const [view, setView] = useState<View>('log');
  const [entries, setEntries] = useState<PainEntry[]>([]);
  const [queued, setQueued] = useState<NewPainEntry[]>([]);
  const [draft, setDraft] = useState<EntryDraft>(emptyDraft());
  const [notice, setNotice] = useState<Notice | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<PainEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PainEntry | null>(null);
  const [followUp, setFollowUp] = useState<{ entry: PainEntry; interventionId: string } | null>(null);
  const online = useConnectivity();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [remote, pending] = await Promise.all([listEntries(), listQueuedEntries()]);
      setEntries(remote.entries);
      setQueued(pending);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setSession('locked');
      else {
        const pending = await listQueuedEntries().catch(() => []);
        setQueued(pending);
        setNotice({ kind: 'warning', text: 'Private storage is unreachable. New check-ins will stay safely queued on this phone.' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const enrichWeather = useCallback(async (entryId: string) => {
    try {
      const { entry } = await addWeather(entryId);
      setEntries((items) => replaceEntry(items, entry));
      return;
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== 'weather_location_unavailable') {
        void markWeatherUnavailable(entryId).then(({ entry }) => setEntries((items) => replaceEntry(items, entry))).catch(() => undefined);
        return;
      }
    }
    if (!('geolocation' in navigator)) {
      void markWeatherUnavailable(entryId).then(({ entry }) => setEntries((items) => replaceEntry(items, entry))).catch(() => undefined);
      return;
    }
    let settled = false;
    const watchdog = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      void markWeatherUnavailable(entryId)
        .then(({ entry }) => setEntries((items) => replaceEntry(items, entry)))
        .catch(() => undefined);
    }, 12_000);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(watchdog);
        void addWeather(entryId, position.coords.latitude, position.coords.longitude)
          .then(({ entry }) => setEntries((items) => replaceEntry(items, entry)))
          .catch(() => markWeatherUnavailable(entryId)
            .then(({ entry }) => setEntries((items) => replaceEntry(items, entry)))
            .catch(() => undefined));
      },
      () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(watchdog);
        void markWeatherUnavailable(entryId)
          .then(({ entry }) => setEntries((items) => replaceEntry(items, entry)))
          .catch(() => undefined);
      },
      { enableHighAccuracy: false, maximumAge: 30 * 60 * 1000, timeout: 8000 },
    );
  }, []);

  const syncQueued = useCallback(async () => {
    if (!online || session !== 'ready') return;
    const pending = await listQueuedEntries().catch(() => []);
    if (!pending.length) return;
    let synced = 0;
    for (const item of pending) {
      try {
        const result = await createEntry(item);
        await removeQueuedEntry(item.id);
        setEntries((entriesNow) => replaceEntry(entriesNow, result.entry));
        void enrichWeather(item.id);
        synced += 1;
      } catch {
        break;
      }
    }
    const remaining = await listQueuedEntries().catch(() => pending);
    setQueued(remaining);
    if (synced) setNotice({ kind: 'success', text: `${synced} queued check-in${synced === 1 ? '' : 's'} synced safely.` });
  }, [enrichWeather, online, session]);

  useEffect(() => {
    getSession()
      .then((result) => {
        const next = result.authenticated ? 'ready' : 'locked';
        setSession(next);
        if (next === 'ready') void load();
        else setLoading(false);
      })
      .catch(() => {
        setSession('locked');
        setLoading(false);
      });
  }, [load]);

  useEffect(() => {
    // This effect intentionally reacts to browser connectivity and session changes.
    // oxlint-disable-next-line react/react-compiler
    if (online && session === 'ready') void syncQueued();
  }, [online, session, syncQueued]);

  useEffect(() => {
    if (!online || session !== 'ready' || queued.length === 0) return;
    const tryAgain = () => {
      if (document.visibilityState === 'visible') void syncQueued();
    };
    const interval = window.setInterval(tryAgain, 30_000);
    window.addEventListener('focus', tryAgain);
    document.addEventListener('visibilitychange', tryAgain);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', tryAgain);
      document.removeEventListener('visibilitychange', tryAgain);
    };
  }, [online, queued.length, session, syncQueued]);

  useEffect(() => {
    const savedPreference = window.localStorage.getItem('cynthia-entered-by');
    if (savedPreference === 'self' || savedPreference === 'caregiver') {
      // Restoring this device preference is an intentional post-hydration update.
      // oxlint-disable-next-line react/react-compiler
      setDraft((current) => ({ ...current, enteredBy: savedPreference }));
    }
  }, []);

  function selectEnteredBy(value: EnteredBy) {
    window.localStorage.setItem('cynthia-entered-by', value);
    setDraft((current) => ({ ...current, enteredBy: value }));
  }

  async function saveCheckIn() {
    if (draft.painLevel === null || !draft.bodyRegions.length || !draft.painTypes.length) return;
    setSaving(true);
    setNotice(null);
    let payload: NewPainEntry;
    try {
      payload = createPayload(draft);
    } catch {
      setNotice({ kind: 'error', text: 'This check-in could not be prepared. Your selections are still here so you can try again.' });
      setSaving(false);
      return;
    }
    try {
      const result = await createEntry(payload);
      setEntries((items) => replaceEntry(items, result.entry));
      setDraft(emptyDraft(draft.enteredBy));
      setNotice({ kind: 'success', text: 'Check-in saved.' });
      void enrichWeather(payload.id);
    } catch (error) {
      if (!(error instanceof ApiError) || error.status >= 500 || error.status === 401) {
        try {
          await queueEntry(payload);
          setQueued((items) => [payload, ...items.filter((item) => item.id !== payload.id)]);
          setDraft(emptyDraft(draft.enteredBy));
          if (error instanceof ApiError && error.status === 401) {
            setSession('locked');
          } else {
            setNotice({ kind: 'warning', text: 'Saved on this phone. It will sync automatically when private storage reconnects.' });
          }
        } catch {
          setNotice({ kind: 'error', text: 'This check-in could not be saved. Your selections are still here so you can try again.' });
        }
      } else {
        setNotice({ kind: 'error', text: apiMessage(error) });
      }
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit(entry: PainEntry) {
    setSaving(true);
    try {
      const result = await updateEntry(entry);
      setEntries((items) => replaceEntry(items, result.entry));
      setEditing(null);
      setNotice({ kind: 'success', text: 'Changes saved.' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setSession('locked');
      else setNotice({ kind: 'error', text: apiMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setSaving(true);
    try {
      await deleteEntry(target.id);
      setEntries((items) => items.filter((item) => item.id !== target.id));
      setDeleteTarget(null);
      setNotice({ kind: 'success', text: 'Entry removed.', undoId: target.id });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setSession('locked');
      else setNotice({ kind: 'error', text: apiMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  async function undoDelete(id: string) {
    try {
      const result = await restoreEntry(id);
      setEntries((items) => replaceEntry(items, result.entry));
      setNotice({ kind: 'success', text: 'Entry restored.' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setSession('locked');
      else setNotice({ kind: 'error', text: apiMessage(error) });
    }
  }

  async function finishFollowUp(afterPain: number) {
    if (!followUp) return;
    setSaving(true);
    try {
      const result = await completeIntervention(followUp.interventionId, afterPain);
      setEntries((items) => replaceEntry(items, result.entry));
      setFollowUp(null);
      setNotice({ kind: 'success', text: 'Response added. This will appear in the summary.' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setSession('locked');
      else setNotice({ kind: 'error', text: apiMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  if (session === 'checking') return <LoadingScreen />;
  if (session === 'locked') return <UnlockScreen onUnlocked={() => { setSession('ready'); void load(); }} />;

  const queuedEntries = queued.map(queuedAsEntry);
  const visibleEntries = [...new Map(
    [...queuedEntries, ...entries].map((entry) => [entry.id, entry]),
  ).values()].sort((a, b) => b.occurredAtUtc.localeCompare(a.occurredAtUtc));
  const pendingFollowUps = entries.flatMap((entry) =>
    entry.interventions
      .filter((intervention) => intervention.afterPain === null)
      .map((intervention) => ({ entry, intervention })),
  );

  return (
    <main className="min-h-dvh bg-background pb-[calc(5.5rem+env(safe-area-inset-bottom))] text-foreground print:bg-white print:pb-0">
      <div className="mx-auto w-full max-w-md px-4 pb-8 pt-[max(1rem,env(safe-area-inset-top))] print:max-w-none print:px-0 print:pt-0">
        <AppHeader enteredBy={draft.enteredBy} onEnteredBy={selectEnteredBy} compact={view === 'report'} />

        {!online ? (
          <div className="mb-4 flex items-center gap-2 rounded-2xl border border-warning/25 bg-warning-soft p-3 text-sm text-warning-foreground print:hidden">
            <WifiOff aria-hidden="true" className="size-4 shrink-0" />Offline. New check-ins will wait safely on this phone.
          </div>
        ) : null}
        {notice ? <NoticeBanner notice={notice} onClose={() => setNotice(null)} onUndo={undoDelete} /> : null}

        {view === 'log' ? (
          <LogView
            draft={draft}
            setDraft={setDraft}
            saving={saving}
            pendingFollowUps={pendingFollowUps}
            onSave={saveCheckIn}
            onFollowUp={(entry, interventionId) => setFollowUp({ entry, interventionId })}
          />
        ) : null}
        {view === 'history' ? (
          <HistoryView
            entries={visibleEntries}
            loading={loading}
            onRefresh={load}
            onEdit={setEditing}
            onDelete={setDeleteTarget}
            onRemoveQueued={async (id) => {
              await removeQueuedEntry(id);
              setQueued((items) => items.filter((item) => item.id !== id));
              setNotice({ kind: 'success', text: 'Queued check-in removed.' });
            }}
          />
        ) : null}
        {view === 'summary' ? <SummaryView entries={entries} /> : null}
        {view === 'report' ? <ReportView entries={entries} /> : null}
      </div>

      <BottomNavigation view={view} onView={(nextView) => { setNotice(null); setView(nextView); }} />

      <EditEntryDialog
        key={editing?.id ?? 'closed-edit-dialog'}
        entry={editing}
        saving={saving}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
      />
      <DeleteEntryDialog
        entry={deleteTarget}
        saving={saving}
        onClose={() => setDeleteTarget(null)}
        onDelete={confirmDelete}
      />
      <FollowUpDialog
        key={followUp ? `${followUp.entry.id}:${followUp.interventionId}` : 'closed-follow-up-dialog'}
        followUp={followUp}
        saving={saving}
        onClose={() => setFollowUp(null)}
        onComplete={finishFollowUp}
      />
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="grid min-h-dvh place-items-center bg-background p-6 text-foreground">
      <output className="block text-center">
        <span className="mx-auto grid size-14 place-items-center rounded-[1.25rem] bg-primary text-primary-foreground">
          <HeartPulse aria-hidden="true" className="size-7" />
        </span>
        <p className="mt-4 text-sm font-semibold">Opening Cynthia Health…</p>
      </output>
    </main>
  );
}

function UnlockScreen({ onUnlocked }: { onUnlocked: () => void }) {
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: { preventDefault: () => void }) {
    event.preventDefault();
    if (!/^\d{8}$/.test(code)) {
      setMessage('Enter the eight-digit access code.');
      return;
    }
    setBusy(true);
    try {
      await unlock(code);
      onUnlocked();
    } catch (error) {
      setMessage(apiMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-background p-5 text-foreground">
      <form onSubmit={submit} className="w-full max-w-sm rounded-[1.75rem] border border-border bg-card p-6 shadow-[0_20px_60px_-35px_rgba(49,74,69,0.55)]">
        <span className="grid size-12 place-items-center rounded-2xl bg-primary-soft text-primary">
          <LockKeyhole aria-hidden="true" className="size-6" />
        </span>
        <h1 className="mt-5 text-2xl font-semibold tracking-tight">Private access</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Enter the access code once on this device. Cynthia Health will remember it.</p>
        <label htmlFor="access-code" className="mt-5 block text-sm font-semibold">Eight-digit code</label>
        <Input
          id="access-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{8}"
          maxLength={8}
          value={code}
          onChange={(event) => { setCode(event.target.value.replace(/\D/g, '').slice(0, 8)); setMessage(''); }}
          className="mt-2 h-14 rounded-xl bg-surface px-4 text-center text-xl font-semibold tracking-[0.3em]"
          aria-describedby={message ? 'access-error' : undefined}
          aria-invalid={Boolean(message)}
        />
        {message ? <p id="access-error" className="mt-2 text-sm text-destructive" role="alert">{message}</p> : null}
        <Button type="submit" disabled={busy || code.length !== 8} className="mt-5 h-13 w-full rounded-xl text-base">
          {busy ? 'Checking…' : 'Open Cynthia Health'}
        </Button>
      </form>
    </main>
  );
}

function AppHeader({
  enteredBy,
  onEnteredBy,
  compact,
}: {
  enteredBy: EnteredBy;
  onEnteredBy: (value: EnteredBy) => void;
  compact: boolean;
}) {
  return (
    <header className={`mb-6 flex justify-between gap-3 print:mb-4 print:flex-row print:items-center print:border-b print:pb-4 ${compact ? 'items-center' : 'flex-col items-stretch min-[400px]:flex-row min-[400px]:items-center'}`}>
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
          <HeartPulse aria-hidden="true" className="size-5" strokeWidth={2.25} />
        </span>
        <div className="min-w-0">
          <p className="whitespace-nowrap text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Cynthia Health</p>
          <p className="whitespace-nowrap text-sm font-semibold">{compact ? 'Clinician summary' : 'A quick check-in'}</p>
        </div>
      </div>
      {!compact ? (
        <div className="flex w-full shrink-0 rounded-full bg-muted p-1 min-[400px]:w-auto print:hidden" aria-label="Who is entering this?">
          {(['self', 'caregiver'] as const).map((person) => (
            <Button
              key={person}
              type="button"
              variant="ghost"
              aria-pressed={enteredBy === person}
              aria-label={`Entered by ${labelForEnteredBy(person)}`}
              onClick={() => onEnteredBy(person)}
              className="h-11 flex-1 rounded-full px-3 text-xs text-muted-foreground min-[400px]:flex-none aria-pressed:bg-card aria-pressed:text-foreground aria-pressed:shadow-sm"
            >
              {person === 'self' ? <UserRound aria-hidden="true" className="size-3.5" /> : null}
              {person === 'self' ? 'Self' : 'Caregiver'}
            </Button>
          ))}
        </div>
      ) : null}
    </header>
  );
}

function NoticeBanner({ notice, onClose, onUndo }: { notice: Notice; onClose: () => void; onUndo: (id: string) => void }) {
  const styles = notice.kind === 'success'
    ? 'border-success/25 bg-success-soft text-success-foreground'
    : notice.kind === 'warning'
      ? 'border-warning/25 bg-warning-soft text-warning-foreground'
      : 'border-destructive/25 bg-destructive/10 text-destructive';
  const Icon = notice.kind === 'success' ? Check : notice.kind === 'warning' ? AlertCircle : AlertCircle;
  return (
    <div role={notice.kind === 'error' ? 'alert' : 'status'} className={`mb-4 flex items-center gap-3 rounded-2xl border p-3.5 text-sm ${styles} print:hidden`}>
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">{notice.text}</span>
      {notice.undoId ? (
        <Button type="button" variant="ghost" onClick={() => onUndo(notice.undoId!)} className="h-11 rounded-full px-3 text-current">
          <RotateCcw aria-hidden="true" className="size-3.5" />Undo
        </Button>
      ) : null}
      <Button type="button" variant="ghost" onClick={onClose} aria-label="Dismiss message" className="size-11 shrink-0 rounded-full p-0 text-current">
        <X aria-hidden="true" />
      </Button>
    </div>
  );
}

function LogView({
  draft,
  setDraft,
  saving,
  pendingFollowUps,
  onSave,
  onFollowUp,
}: {
  draft: EntryDraft;
  setDraft: React.Dispatch<React.SetStateAction<EntryDraft>>;
  saving: boolean;
  pendingFollowUps: Array<{ entry: PainEntry; intervention: PainEntry['interventions'][number] }>;
  onSave: () => void;
  onFollowUp: (entry: PainEntry, interventionId: string) => void;
}) {
  const [extrasOpen, setExtrasOpen] = useState(false);
  const otherLocationReady = !draft.bodyRegions.includes('Other') || Boolean(draft.otherBodyRegion.trim());
  const ready = draft.painLevel !== null && draft.bodyRegions.length > 0 && draft.painTypes.length > 0 && otherLocationReady;
  return (
    <>
      <section id="log" className="rounded-[1.75rem] border border-border/80 bg-card p-5 shadow-[0_18px_55px_-32px_rgba(49,74,69,0.45)]">
        <div className="mb-5">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Right now</p>
            <h1 className="mt-1 text-[1.7rem] font-semibold leading-tight tracking-[-0.035em]">How is your pain?</h1>
          </div>
        </div>

        <fieldset>
          <legend className="mb-2.5 text-sm font-semibold">Pain level</legend>
          <PainLevelGrid value={draft.painLevel} onChange={(painLevel) => setDraft((current) => ({ ...current, painLevel }))} />
        </fieldset>

        <ChoiceFieldset
          legend="Where?"
          values={BODY_REGIONS}
          selected={draft.bodyRegions}
          onToggle={(value) => setDraft((current) => ({
            ...current,
            bodyRegions: toggleItem(current.bodyRegions, value),
            otherBodyRegion: value === 'Other' && current.bodyRegions.includes('Other') ? '' : current.otherBodyRegion,
          }))}
        />
        {draft.bodyRegions.includes('Other') ? (
          <div className="mt-3">
            <label htmlFor="other-body-region" className="block text-sm font-semibold">Where specifically?</label>
            <Input
              id="other-body-region"
              value={draft.otherBodyRegion}
              maxLength={120}
              onChange={(event) => setDraft((current) => ({ ...current, otherBodyRegion: event.target.value }))}
              placeholder="Type the body location"
              className="mt-2 h-12 rounded-xl bg-surface text-base"
            />
          </div>
        ) : null}
        <ChoiceFieldset
          legend="What does it feel like?"
          values={PAIN_TYPES}
          selected={draft.painTypes}
          onToggle={(value) => setDraft((current) => ({ ...current, painTypes: toggleItem(current.painTypes, value) }))}
        />
        <label htmlFor="entry-notes" className="mt-5 block text-sm font-semibold">Note about this check-in <span className="font-normal text-muted-foreground">(optional)</span></label>
        <Textarea
          id="entry-notes"
          value={draft.notes}
          maxLength={1000}
          onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
          placeholder="Anything Cynthia wants to remember"
          className="mt-2 min-h-20 resize-y rounded-xl bg-surface text-base"
        />
        <details
          open={extrasOpen}
          onToggle={(event) => setExtrasOpen(event.currentTarget.open)}
          className="mt-5 rounded-xl border border-border bg-surface px-4 py-0 open:pb-4"
        >
          <summary className="flex min-h-11 cursor-pointer select-none items-center text-sm font-semibold">Add a treatment <span className="ml-1 font-normal text-muted-foreground">(optional)</span></summary>
          <ChoiceFieldset
            legend="What are you trying?"
            hint="We’ll ask for an after-rating later."
            values={INTERVENTION_TYPES}
            selected={draft.interventions}
            onToggle={(value) => setDraft((current) => ({ ...current, interventions: toggleItem(current.interventions, value) }))}
          />
        </details>

        <Button
          type="button"
          disabled={!ready || saving}
          onClick={() => { setExtrasOpen(false); onSave(); }}
          className="mt-6 h-14 w-full rounded-2xl text-base font-semibold shadow-[0_10px_24px_-12px_rgba(42,92,81,0.75)]"
        >
          {saving ? 'Saving…' : ready ? 'Save check-in' : !otherLocationReady ? 'Describe the other location' : 'Choose level, place, and feeling'}
        </Button>
        <p className="mt-3 text-center text-xs leading-relaxed text-muted-foreground">Time and weather context are added automatically.</p>
      </section>

      {pendingFollowUps.length ? (
        <section className="mt-4 rounded-[1.5rem] border border-accent-strong/20 bg-accent p-4 shadow-sm">
          <p className="text-sm font-semibold text-accent-foreground">How did it help?</p>
          <div className="mt-2 space-y-2">
            {pendingFollowUps.map(({ entry, intervention }) => (
              <Button
                key={intervention.id}
                type="button"
                variant="ghost"
                onClick={() => onFollowUp(entry, intervention.id)}
                className="h-auto min-h-12 w-full justify-between rounded-xl bg-card/70 px-3 py-2 text-left text-accent-foreground"
              >
                <span className="min-w-0">
                  <span className="block font-semibold">{intervention.type}</span>
                  <span className="block truncate text-xs font-normal opacity-70">Started at pain {intervention.beforePain}/10 · {formatWhen(intervention.startedAtUtc)}</span>
                </span>
                <ChevronRight aria-hidden="true" className="size-4 shrink-0" />
              </Button>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function PainLevelGrid({ value, onChange }: { value: number | null; onChange: (value: number) => void }) {
  const selectedGuide = value === null ? null : PAIN_GUIDE[value];
  return (
    <>
      <div className="grid grid-cols-5 gap-1 min-[360px]:grid-cols-6" aria-label="Pain level from 0 to 10">
        {painLevels.map((level) => (
          <Button
            key={level}
            type="button"
            variant="outline"
            aria-pressed={value === level}
            aria-label={`${level} out of 10${level === 0 ? ', no pain' : level === 10 ? ', worst pain' : ''}`}
            onClick={() => onChange(level)}
            className="h-12 min-w-0 rounded-xl border-border bg-surface text-base font-semibold aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
          >
            {level}
          </Button>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between px-1 text-[0.68rem] font-medium text-muted-foreground">
        <span>No pain</span><span>Worst pain</span>
      </div>
      {selectedGuide ? (
        <div className="mt-3 rounded-xl border border-primary/15 bg-primary-soft p-3 text-sm leading-relaxed text-primary">
          <p className="font-semibold">{selectedGuide.level} · {selectedGuide.label}</p>
          <p className="mt-0.5 text-xs text-foreground/75">{selectedGuide.description}</p>
        </div>
      ) : null}
      <details className="mt-2 rounded-xl border border-border bg-surface px-3">
        <summary className="flex min-h-11 cursor-pointer select-none items-center text-xs font-semibold text-muted-foreground">View the full 0–10 guide</summary>
        <ol className="space-y-2 border-t border-border py-3">
          {PAIN_GUIDE.map((item) => (
            <li key={item.level} className="grid grid-cols-[1.75rem_1fr] gap-2 text-xs leading-relaxed">
              <span className="font-bold text-primary">{item.level}</span>
              <span><strong>{item.label}.</strong> {item.description}</span>
            </li>
          ))}
        </ol>
      </details>
    </>
  );
}

function ChoiceFieldset({
  legend,
  hint,
  values,
  selected,
  onToggle,
}: {
  legend: string;
  hint?: string;
  values: readonly string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <fieldset className="mt-5">
      <legend className="text-sm font-semibold">{legend}</legend>
      {hint ? <p className="mb-2.5 mt-0.5 text-xs text-muted-foreground">{hint}</p> : <div className="h-2.5" />}
      <div className="flex flex-wrap gap-2">
        {values.map((value) => (
          <Button
            key={value}
            type="button"
            variant="outline"
            aria-pressed={selected.includes(value)}
            onClick={() => onToggle(value)}
            className="h-11 rounded-full border-border bg-surface px-4 text-sm aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary"
          >
            {value}
          </Button>
        ))}
      </div>
    </fieldset>
  );
}

function HistoryView({
  entries,
  loading,
  onRefresh,
  onEdit,
  onDelete,
  onRemoveQueued,
}: {
  entries: PainEntry[];
  loading: boolean;
  onRefresh: () => void;
  onEdit: (entry: PainEntry) => void;
  onDelete: (entry: PainEntry) => void;
  onRemoveQueued: (id: string) => void;
}) {
  return (
    <section id="history">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Saved check-ins</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">History</h1>
        </div>
        <Button type="button" variant="outline" onClick={onRefresh} disabled={loading} className="h-11 rounded-full bg-card px-4">
          <RefreshCw aria-hidden="true" className={`size-4 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>
      {!entries.length && !loading ? (
        <EmptyState icon={History} title="No check-ins yet" text="Your first saved check-in will appear here, ready to edit if anything needs correcting." />
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <article key={entry.id} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary-soft text-lg font-bold text-primary" aria-label={`Pain ${entry.painLevel} out of 10`}>
                  {entry.painLevel}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{formatWhen(entry.occurredAtUtc)}</p>
                    {entry.syncStatus === 'queued' ? <Badge variant="secondary">Waiting to sync</Badge> : null}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{bodyRegionsText(entry)} · {entry.painTypes.join(', ')}</p>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>{labelForEnteredBy(entry.enteredBy)}</span>
                    <span className="inline-flex items-center gap-1">
                      {entry.weather.status === 'unavailable' ? <CloudOff aria-hidden="true" className="size-3.5" /> : <CloudSun aria-hidden="true" className="size-3.5" />}
                      {weatherText(entry)}
                    </span>
                  </div>
                  {entry.interventions.length ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {entry.interventions.map((item) => (
                        <Badge key={item.id} variant="outline" className={item.afterPain === null ? 'border-accent-strong/30 bg-accent' : ''}>
                          {item.type}{item.afterPain === null ? ' · follow-up' : ` · ${item.beforePain}→${item.afterPain}`}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                  {entry.notes ? <p className="mt-3 rounded-xl bg-surface p-3 text-sm leading-relaxed">{entry.notes}</p> : null}
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2 border-t border-border/70 pt-3">
                {entry.syncStatus === 'queued' ? (
                  <Button type="button" variant="ghost" onClick={() => onRemoveQueued(entry.id)} className="h-11 rounded-full px-3 text-destructive">
                    <Trash2 aria-hidden="true" className="size-4" />Remove pending
                  </Button>
                ) : (
                  <>
                    <Button type="button" variant="ghost" onClick={() => onEdit(entry)} className="h-11 rounded-full px-3">
                      <Pencil aria-hidden="true" className="size-4" />Edit
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => onDelete(entry)} className="h-11 rounded-full px-3 text-destructive">
                      <Trash2 aria-hidden="true" className="size-4" />Delete
                    </Button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function SummaryView({ entries }: { entries: PainEntry[] }) {
  const [days, setDays] = useState<7 | 30>(7);
  const periodEntries = useMemo(() => entriesWithinDays(entries, days), [days, entries]);
  const summary = useMemo(() => summarizeEntries(periodEntries), [periodEntries]);
  return (
    <section id="summary">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Patterns, not judgments</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Summary</h1>
        </div>
        <div className="flex rounded-full bg-muted p-1" aria-label="Summary range">
          {([7, 30] as const).map((range) => (
            <Button key={range} type="button" variant="ghost" aria-pressed={days === range} onClick={() => setDays(range)} className="h-11 rounded-full px-3 text-xs aria-pressed:bg-card aria-pressed:shadow-sm">
              {range} days
            </Button>
          ))}
        </div>
      </div>
      {!summary.entryCount ? (
        <EmptyState icon={LineChart} title={`No check-ins in the last ${days} days`} text="Once check-ins are saved, this page will summarize pain levels, common locations, and intervention responses." />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2.5">
            <Metric label="Check-ins" value={String(summary.entryCount)} />
            <Metric label="Average" value={`${summary.averagePain}/10`} />
            <Metric label="Range" value={`${summary.lowPain}–${summary.highestPain}`} />
          </div>
          <section className="mt-4 rounded-2xl border border-border bg-card p-4">
            <h2 className="font-semibold">Most common</h2>
            <div className="mt-3 grid gap-4 min-[380px]:grid-cols-2">
              <RankedList title="Body regions" items={summary.commonRegions} />
              <RankedList title="Pain types" items={summary.commonPainTypes} />
            </div>
          </section>
          <section className="mt-4 rounded-2xl border border-border bg-card p-4">
            <h2 className="font-semibold">Intervention response</h2>
            {summary.interventionResponses.length ? (
              <div className="mt-3 space-y-2">
                {summary.interventionResponses.map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-xl bg-surface p-3 text-sm">
                    <div><p className="font-semibold">{item.label}</p><p className="text-xs text-muted-foreground">{item.completedCount} completed follow-up{item.completedCount === 1 ? '' : 's'}</p></div>
                    <Badge variant={item.averageChange > 0 ? 'default' : 'secondary'}>
                      {item.averageChange > 0 ? `↓ ${item.averageChange}` : item.averageChange < 0 ? `↑ ${Math.abs(item.averageChange)}` : 'No change'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Complete an after-rating to see how pain changed following an intervention.</p>}
          </section>
          <section className="mt-4 rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-2"><CloudSun aria-hidden="true" className="size-4 text-weather-foreground" /><h2 className="font-semibold">Pressure context</h2></div>
            {summary.pressureSamples >= 4 ? (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Across {summary.pressureSamples} weather-linked check-ins, average pain was {summary.lowPressureAveragePain}/10 in the lower-pressure half and {summary.highPressureAveragePain}/10 in the higher-pressure half. This is descriptive, not evidence that pressure caused the change.</p>
            ) : (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">At least four weather-linked check-ins are needed before showing a pressure comparison.</p>
            )}
          </section>
        </>
      )}
    </section>
  );
}

function ReportView({ entries }: { entries: PainEntry[] }) {
  const periodEntries = useMemo(() => entriesWithinDays(entries, 30), [entries]);
  const summary = useMemo(() => summarizeEntries(periodEntries), [periodEntries]);
  const generated = formatWhen(new Date().toISOString(), { month: 'long', day: 'numeric', year: 'numeric' });
  return (
    <section id="report" className="print:text-black">
      <div className="mb-4 flex flex-col items-stretch justify-between gap-3 min-[360px]:flex-row min-[360px]:items-start print:mb-6 print:flex-row print:items-start">
        <div>
          <p className="text-sm font-medium text-muted-foreground print:text-gray-600">Last 30 days · generated {generated}</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Clinician summary</h1>
        </div>
        <Button type="button" onClick={() => window.print()} className="h-11 w-full rounded-full px-4 min-[360px]:w-auto print:hidden">
          <Printer aria-hidden="true" className="size-4" />Print or save PDF
        </Button>
      </div>
      {!periodEntries.length ? (
        <EmptyState icon={FileText} title="No entries to report yet" text="The report will become available after a check-in is saved." />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2.5 print:grid-cols-3">
            <Metric label="Check-ins" value={String(summary.entryCount)} />
            <Metric label="Avg. pain" value={`${summary.averagePain}/10`} />
            <Metric label="Pain range" value={`${summary.lowPain}–${summary.highestPain}`} />
          </div>
          <section className="mt-4 rounded-2xl border border-border bg-card p-4 print:break-inside-avoid print:border-gray-300 print:bg-white">
            <h2 className="font-semibold">Observed patterns</h2>
            <dl className="mt-3 grid gap-3 text-sm min-[380px]:grid-cols-2">
              <div><dt className="text-muted-foreground print:text-gray-600">Common regions</dt><dd className="mt-0.5 font-medium">{summary.commonRegions.map((item) => `${item.label} (${item.count})`).join(', ') || 'None recorded'}</dd></div>
              <div><dt className="text-muted-foreground print:text-gray-600">Common pain types</dt><dd className="mt-0.5 font-medium">{summary.commonPainTypes.map((item) => `${item.label} (${item.count})`).join(', ') || 'None recorded'}</dd></div>
              <div><dt className="text-muted-foreground print:text-gray-600">Entered by</dt><dd className="mt-0.5 font-medium">Cynthia: {periodEntries.filter((item) => item.enteredBy === 'self').length} · Caregiver: {periodEntries.filter((item) => item.enteredBy === 'caregiver').length}</dd></div>
              <div><dt className="text-muted-foreground print:text-gray-600">Weather-linked</dt><dd className="mt-0.5 font-medium">{summary.pressureSamples} of {summary.entryCount} check-ins</dd></div>
            </dl>
          </section>
          <section className="mt-4 rounded-2xl border border-border bg-card p-4 print:break-inside-avoid print:border-gray-300 print:bg-white">
            <h2 className="font-semibold">Intervention follow-ups</h2>
            {summary.interventionResponses.length ? (
              <table className="mt-3 w-full text-left text-sm">
                <thead><tr className="border-b"><th className="pb-2 font-medium text-muted-foreground">Intervention</th><th className="pb-2 text-right font-medium text-muted-foreground">Follow-ups</th><th className="pb-2 text-right font-medium text-muted-foreground">Avg. pain change</th></tr></thead>
                <tbody>{summary.interventionResponses.map((item) => <tr key={item.label} className="border-b last:border-0"><td className="py-2 font-medium">{item.label}</td><td className="py-2 text-right">{item.completedCount}</td><td className="py-2 text-right">{item.averageChange > 0 ? `-${item.averageChange}` : item.averageChange < 0 ? `+${Math.abs(item.averageChange)}` : '0'}</td></tr>)}</tbody>
              </table>
            ) : <p className="mt-2 text-sm text-muted-foreground">No completed intervention follow-ups in this period.</p>}
          </section>
          <section className="mt-4 overflow-hidden rounded-2xl border border-border bg-card print:border-gray-300 print:bg-white">
            <h2 className="p-4 pb-3 font-semibold">Recent check-ins</h2>
            <div className="divide-y divide-border">
              {periodEntries.map((entry) => (
                <div key={entry.id} className="grid grid-cols-[2.5rem_1fr] gap-3 p-4 text-sm print:break-inside-avoid">
                  <div className="font-bold">{entry.painLevel}/10</div>
                  <div>
                    <p className="font-medium">{formatWhen(entry.occurredAtUtc)} · {labelForEnteredBy(entry.enteredBy)}</p>
                    <p className="mt-0.5 text-muted-foreground print:text-gray-600">{bodyRegionsText(entry)} · {entry.painTypes.join(', ')}</p>
                    {entry.interventions.length ? <p className="mt-0.5 text-muted-foreground print:text-gray-600">Tried: {entry.interventions.map((item) => `${item.type}${item.afterPain === null ? ' (awaiting follow-up)' : ` (${item.beforePain}→${item.afterPain})`}`).join(', ')}</p> : null}
                    {entry.notes ? <p className="mt-1">{entry.notes}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground print:text-gray-600">This report summarizes self- and caregiver-recorded observations. It does not diagnose causes or replace clinical assessment.</p>
        </>
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-2xl border border-border bg-card p-3 text-center print:border-gray-300 print:bg-white"><p className="text-xl font-bold tracking-tight">{value}</p><p className="mt-0.5 text-[0.68rem] font-medium text-muted-foreground print:text-gray-600">{label}</p></div>;
}

function RankedList({ title, items }: { title: string; items: Array<{ label: string; count: number }> }) {
  return <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p><ol className="mt-2 space-y-2">{items.map((item) => <li key={item.label} className="flex items-center justify-between text-sm"><span>{item.label}</span><Badge variant="secondary">{item.count}</Badge></li>)}</ol></div>;
}

function EmptyState({ icon: Icon, title, text }: { icon: typeof History; title: string; text: string }) {
  return <div className="rounded-[1.75rem] border border-dashed border-border bg-card/70 p-8 text-center"><span className="mx-auto grid size-12 place-items-center rounded-2xl bg-muted text-muted-foreground"><Icon aria-hidden="true" className="size-6" /></span><h2 className="mt-4 font-semibold">{title}</h2><p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">{text}</p></div>;
}

function BottomNavigation({ view, onView }: { view: View; onView: (view: View) => void }) {
  const items = [
    { id: 'log' as const, label: 'Log', icon: HeartPulse },
    { id: 'history' as const, label: 'History', icon: History },
    { id: 'summary' as const, label: 'Summary', icon: LineChart },
    { id: 'report' as const, label: 'Report', icon: FileText },
  ];
  return (
    <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-20 border-t border-border/80 bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl print:hidden">
      <div className="mx-auto grid h-[4.75rem] max-w-md grid-cols-4 px-2">
        {items.map(({ id, label, icon: Icon }) => (
          <Button
            key={id}
            type="button"
            variant="ghost"
            aria-current={view === id ? 'page' : undefined}
            onClick={() => { onView(id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className={`h-full min-h-11 flex-col gap-1 rounded-none text-xs ${view === id ? 'font-semibold text-primary' : 'font-medium text-muted-foreground'}`}
          >
            <Icon aria-hidden="true" className="size-5" />{label}
          </Button>
        ))}
      </div>
    </nav>
  );
}

function EditEntryDialog({ entry, saving, onClose, onSave }: { entry: PainEntry | null; saving: boolean; onClose: () => void; onSave: (entry: PainEntry) => void }) {
  const [working, setWorking] = useState<PainEntry | null>(entry);
  if (!working) return null;
  const valid = working.bodyRegions.length > 0 && working.painTypes.length > 0
    && (!working.bodyRegions.includes('Other') || Boolean(working.otherBodyRegion.trim()));
  return (
    <Dialog open={Boolean(entry)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto rounded-[1.5rem] p-5 sm:max-w-md">
        <DialogHeader><DialogTitle className="text-lg font-semibold">Edit check-in</DialogTitle><DialogDescription>Correct the saved entry. The original timestamp stays unchanged.</DialogDescription></DialogHeader>
        <fieldset><legend className="mb-2 text-sm font-semibold">Pain level</legend><PainLevelGrid value={working.painLevel} onChange={(painLevel) => setWorking({ ...working, painLevel, interventions: working.interventions.map((item) => ({ ...item, beforePain: painLevel })) })} /></fieldset>
        <ChoiceFieldset legend="Where?" values={BODY_REGIONS} selected={working.bodyRegions} onToggle={(value) => setWorking({
          ...working,
          bodyRegions: toggleItem(working.bodyRegions, value),
          otherBodyRegion: value === 'Other' && working.bodyRegions.includes('Other') ? '' : working.otherBodyRegion,
        })} />
        {working.bodyRegions.includes('Other') ? (
          <div className="mt-3">
            <label htmlFor="edit-other-body-region" className="block text-sm font-semibold">Where specifically?</label>
            <Input id="edit-other-body-region" maxLength={120} value={working.otherBodyRegion} onChange={(event) => setWorking({ ...working, otherBodyRegion: event.target.value })} placeholder="Type the body location" className="mt-2 h-12 rounded-xl bg-surface text-base" />
          </div>
        ) : null}
        <ChoiceFieldset legend="What did it feel like?" values={PAIN_TYPES} selected={working.painTypes} onToggle={(value) => setWorking({ ...working, painTypes: toggleItem(working.painTypes, value) })} />
        <ChoiceFieldset
          legend="Treatments tried"
          hint="Add or remove treatments. Existing after-ratings stay with treatments you keep."
          values={INTERVENTION_TYPES}
          selected={working.interventions.map((item) => item.type)}
          onToggle={(value) => setWorking(toggleEntryIntervention(working, value as (typeof INTERVENTION_TYPES)[number]))}
        />
        <fieldset className="mt-5"><legend className="mb-2.5 text-sm font-semibold">Entered by</legend><div className="flex gap-2">{(['self', 'caregiver'] as const).map((value) => <Button key={value} type="button" variant="outline" aria-pressed={working.enteredBy === value} onClick={() => setWorking({ ...working, enteredBy: value })} className="h-11 flex-1 rounded-xl aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary">{labelForEnteredBy(value)}</Button>)}</div></fieldset>
        <label htmlFor="edit-notes" className="mt-5 block text-sm font-semibold">Note about this check-in <span className="font-normal text-muted-foreground">(optional)</span></label>
        <Textarea id="edit-notes" maxLength={1000} value={working.notes} onChange={(event) => setWorking({ ...working, notes: event.target.value })} placeholder="Anything Cynthia wants to remember" className="mt-2 min-h-20 rounded-xl bg-surface text-base" />
        <DialogFooter className="sticky -bottom-5 mt-2 bg-card">
          <Button type="button" variant="outline" onClick={onClose} className="h-11 rounded-xl">Cancel</Button>
          <Button type="button" disabled={!valid || saving} onClick={() => onSave(working)} className="h-11 rounded-xl">{saving ? 'Saving…' : 'Save changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteEntryDialog({ entry, saving, onClose, onDelete }: { entry: PainEntry | null; saving: boolean; onClose: () => void; onDelete: () => void }) {
  return (
    <Dialog open={Boolean(entry)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="rounded-[1.5rem] p-5 sm:max-w-sm">
        <DialogHeader><DialogTitle className="text-lg font-semibold">Delete this check-in?</DialogTitle><DialogDescription>{entry ? `${formatWhen(entry.occurredAtUtc)} · pain ${entry.painLevel}/10. ` : ''}It will disappear from history and summaries. You can undo immediately afterward.</DialogDescription></DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="h-11 rounded-xl">Keep it</Button>
          <Button type="button" variant="destructive" disabled={saving} onClick={onDelete} className="h-11 rounded-xl">{saving ? 'Deleting…' : 'Delete check-in'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpDialog({ followUp, saving, onClose, onComplete }: { followUp: { entry: PainEntry; interventionId: string } | null; saving: boolean; onClose: () => void; onComplete: (afterPain: number) => void }) {
  const [afterPain, setAfterPain] = useState<number | null>(null);
  const intervention = followUp?.entry.interventions.find((item) => item.id === followUp.interventionId);
  return (
    <Dialog open={Boolean(followUp)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="rounded-[1.5rem] p-5 sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">After {intervention?.type ?? 'the intervention'}</DialogTitle>
          <DialogDescription>Pain was {intervention?.beforePain ?? '—'}/10 before. What is it now?</DialogDescription>
        </DialogHeader>
        <fieldset><legend className="sr-only">Pain level after intervention</legend><PainLevelGrid value={afterPain} onChange={setAfterPain} /></fieldset>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="h-11 rounded-xl">Later</Button>
          <Button type="button" disabled={afterPain === null || saving} onClick={() => onComplete(afterPain!)} className="h-11 rounded-xl">{saving ? 'Saving…' : 'Save after-rating'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
